import styled from "styled-components";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { Box } from "../../components/Box";
import { Paragraph } from "../../components/Paragraph";
import { Loader } from "../../components/Loader";
import { Modal } from "../../components/Modal";
import { Toggle } from "../../components/Toggle";
import { ConfirmModal } from "../../components/Modals/ConfirmModal";
import { useMemo, useState } from "react";
import { useAllAdditionalDocuments } from "../../data/useAllAdditionalDocuments";
import theme from "../../theme";
import { Image } from "../../components/Image";
import success from "../../assets/card-success-icon.svg";
import { IndividualAdditionalDocumentType } from "../../types/AdditionalDocumentsType";
import { Form, Formik } from "formik";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { useExtraAction } from "../../data/useExtraAction";
import { useAddExtraAction } from "../../data/useAddExtraAction";

export const Heading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[2]};
  `}
`;

export const TitleHeading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[3]};
    word-break: break-all;
  `}
`;

export const CardLine = ({ children, ...styleProps }: any) => (
  <Paragraph fontSize={1} color="gray.60" my={0} {...styleProps}>
    {children}
  </Paragraph>
);
export const CardLineDetail = ({ children, ...styleProps }: any) => (
  <Paragraph fontSize={2} color="gray.60" my={0} {...styleProps}>
    {children}
  </Paragraph>
);

interface AdditionalDocumentProps {
  isOpen: boolean;
  onRequestClose: () => void;
  mandateId: number;
  individualId: number;
  includedAdditionalDocument: IndividualAdditionalDocumentType[];
  documentType?: "individual" | "entity";
}

export const NewDocumentsRequest = ({
  isOpen,
  onRequestClose,
  includedAdditionalDocument,
  mandateId,
  individualId,
  documentType = "individual",
}: AdditionalDocumentProps) => {
  const { data, isLoading } = useAllAdditionalDocuments(documentType);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [activeAdditionalDocuments, setActiveAdditionalDocuments] = useState(
    []
  );
  const { mutate: addExtraAction } = useAddExtraAction();
  const { mutate: extraAction } = useExtraAction();

  // Filter out included additional documents from data
  const filteredAdditionalDocuments = useMemo(() => {
    if (!data) return [];
    return data.filter(
      (documents) =>
        (documents.name || documents.description) &&
        !includedAdditionalDocument?.some(
          (includedAdditionalDocument) =>
            includedAdditionalDocument?.additional_document?.id === documents.id
        )
    );
  }, [data, includedAdditionalDocument]);

  const getInitialValues = () => {
    if (!filteredAdditionalDocuments) return {};
    const initialValues = {};
    filteredAdditionalDocuments.forEach((item) => {
      initialValues[`active_${item.id}`] = false;
    });
    return initialValues;
  };

  const handleActiveChange = (id, isAdded) => {
    setActiveAdditionalDocuments((prev) => {
      if (isAdded) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        return prev.filter((documentId) => documentId !== id);
      }
    });
  };

  const handleSubmit = (documentArray) => {
    const combinedDocuments = [
      ...includedAdditionalDocument.map(
        (option) => option.additional_document.id
      ),
      ...documentArray,
    ];
    const uniqueDocumentsMap = new Map();
    combinedDocuments.forEach((doc) => {
      if (!uniqueDocumentsMap.has(doc.id)) {
        uniqueDocumentsMap.set(doc.id, doc);
      }
    });

    const uniqueCombinedDocuments = Array.from(combinedDocuments.values());

    const obj = {
      additional_document: uniqueCombinedDocuments,
    };

    addExtraAction(
      {
        id: individualId,
        data: {
          ...obj,
        },
      },
      {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2")) {
            // notify("Document(s) added successfully!", {
            //   type: "success",
            // });
          }
          extraAction(
            {
              action: "add_additional_document",
              id: individualId,
            },
            {
              onError: (err: any) => {
                showError(
                  err,
                  "A problem occurred while adding additional document."
                );
              },
              onSuccess: (res) => {
                if (res?.status?.toString().startsWith("2")) {
                  notify("Additional document(s) added successfully!", {
                    type: "success",
                  });
                }

                setActiveAdditionalDocuments([]);
                //   onRequestClose();

                setIsConfirmModalOpen(false);
                queryClient.invalidateQueries(["mandate"]);
                queryClient.invalidateQueries(["individual"]);
                queryClient.invalidateQueries(["extra_action"]);
              },
            }
          );
        },
        onError: (err) => {
          showError(err, "Error occurred while adding form(s)");
        },
      }
    );
  };

  const reset = () => {
    setActiveAdditionalDocuments([]);
    onRequestClose();
    setSelectAll(false);
  };

  const addRequestedDocument = () => {
    setIsConfirmModalOpen(false);
    onRequestClose();
    handleSubmit(activeAdditionalDocuments);
  };
  return (
    <>
      <Modal width="700px" isOpen={isOpen} onClose={reset}>
        <>
          {isLoading && (
            <Flex justifyContent="center" alignItems="center" minHeight="25vh">
              <Loader />
            </Flex>
          )}
          {!isLoading && (
            <Box bg="white" borderRadius="16px" overflow="hidden">
              <Flex justifyContent="space-between" alignItems="center">
                <H2 fontSize="3">Additional Documents</H2>
              </Flex>
              <H3 fontSize="16px">Existing Additional Documents</H3>
              <Paragraph fontSize={1} my={0} color="gray.60">
                Selected {includedAdditionalDocument?.length}
              </Paragraph>
              <Box mb={3}>
                {includedAdditionalDocument?.length === 0 ? (
                  <Paragraph color={"gray.60"} fontSize={1}>
                    No additional documents have been added to this application
                    type yet
                  </Paragraph>
                ) : (
                  includedAdditionalDocument?.map((item, index) => (
                    <Flex
                      key={index}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      borderBottom={"1px solid"}
                      borderBottomColor="gray.10"
                      py={2}
                    >
                      <Box>
                        <H3 fontSize={2} mb={"0px"}>
                          {item?.additional_document?.name}
                        </H3>
                        <Paragraph my={0} color={"gray.60"} fontSize={1}>
                          {item?.additional_document?.description}
                        </Paragraph>
                      </Box>
                      <Box>
                        <Image
                          src={success}
                          alt="icon"
                          height="20px"
                          width="auto"
                          mr={1}
                        />
                      </Box>
                    </Flex>
                  ))
                )}
              </Box>
              {includedAdditionalDocument?.length === 0 && (
                <hr color={theme.colors.gray[100]} />
              )}
              <H3 fontSize="16px">Request Additional Document</H3>
              <Paragraph fontSize={1} my={0} color="gray.60">
                You can add additional document(s) to this application. These
                additional document(s) will need to be submitted by your
                customer as part of the verification process.
              </Paragraph>

              {isLoading ? (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  minHeight="25vh"
                >
                  <Loader />
                </Flex>
              ) : (
                <Formik
                  initialValues={getInitialValues()}
                  enableReinitialize
                  validate={() => null}
                  onSubmit={(values, actions) => {
                    actions.setSubmitting(false);
                  }}
                >
                  {(props) => (
                    <Form>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                      >
                        <Paragraph fontSize={1} color="gray.60">
                          Selected {activeAdditionalDocuments.length}
                        </Paragraph>
                        <Flex alignItems="center" gap="2">
                          <Paragraph fontSize={1} color="gray.60" mr={2}>
                            Select all
                          </Paragraph>
                          <Toggle
                            isChecked={selectAll}
                            isDisabled={
                              filteredAdditionalDocuments?.length === 0
                            }
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setSelectAll(isChecked);

                              filteredAdditionalDocuments?.forEach((item) => {
                                props.setFieldValue(
                                  `active_${item.id}`,
                                  isChecked
                                );
                                handleActiveChange(item.id, isChecked);
                              });
                            }}
                          />
                        </Flex>
                      </Flex>

                      <Box>
                        {filteredAdditionalDocuments.map((item, index) => (
                          <Flex
                            key={item.id}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            borderBottom={"1px solid"}
                            borderBottomColor="gray.10"
                            py={2}
                          >
                            <Box>
                              <H3 fontSize={2} mb={"0px"}>
                                {item.name}
                              </H3>
                              <Paragraph my={0} color={"gray.60"} fontSize={1}>
                                {item.description}
                              </Paragraph>
                            </Box>
                            <Box>
                              <Toggle
                                isChecked={
                                  props.values[`active_${item.id}`] || false
                                }
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  props.setFieldValue(
                                    `active_${item.id}`,
                                    isChecked
                                  );
                                  handleActiveChange(item.id, isChecked);
                                }}
                                mt={2}
                              />
                            </Box>
                          </Flex>
                        ))}
                        {filteredAdditionalDocuments?.length === 0 && (
                          <Paragraph fontSize={1}>
                            No further additional documents available
                          </Paragraph>
                        )}
                      </Box>

                      <Flex py={4} justifyContent="end">
                        <Button
                          size="large"
                          mt="3"
                          mb="0"
                          mr="2"
                          variant="secondary"
                          onClick={() => reset()}
                        >
                          Back
                        </Button>
                        <Button
                          size="large"
                          mt="3"
                          mb="0"
                          onClick={() => setIsConfirmModalOpen(true)}
                          isDisabled={
                            !props.dirty ||
                            activeAdditionalDocuments.length === 0
                          }
                        >
                          Add
                        </Button>
                      </Flex>
                    </Form>
                  )}
                </Formik>
              )}
            </Box>
          )}
        </>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onRequestClose={() => {
         // setActiveAdditionalDocuments([]);
          setIsConfirmModalOpen(false);
        }}
        handleConfirm={addRequestedDocument}
        title="Request additional document(s)?"
        content="An email will be sent to the individual asking them to upload these documents through the mobile app."
      />
    </>
  );
};
